import { useEffect, useState, FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type Message = { id: number; sender: string; message: string; createdAt: string; user: { firstName: string | null; lastName: string | null; email: string } | null };
type Attachment = { id: number; fileName: string; fileKey: string };
type Claim = { id: number; subject: string; description: string; status: string; priority: string; createdAt: string; adminNotes: string | null; registration: { serialNumber: string; product: { name: string } | null } | null; claimMessages: Message[]; claimAttachments: Attachment[] };

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700', in_review: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700', resolved: 'bg-gray-100 text-gray-500',
};

export default function ClaimDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [claim, setClaim]   = useState<Claim | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply]   = useState('');
  const [sending, setSending] = useState(false);
  const [file, setFile]     = useState<File | null>(null);

  const load = () => api.get<Claim>(`/claims/${id}`).then(c => { setClaim(c); setLoading(false); }).catch(() => setLoading(false));

  useEffect(() => { load(); }, [id]);

  async function sendReply(e: FormEvent) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    await api.post(`/claims/${id}/reply`, { message: reply });
    setReply('');
    await load();
    setSending(false);
  }

  async function uploadAttachment() {
    if (!file) return;
    await api.upload(`/claims/${id}/upload`, file);
    setFile(null);
    await load();
  }

  if (loading) return <Layout><p className="text-gray-400">{t('common.loading')}</p></Layout>;
  if (!claim)  return <Layout><p className="text-red-500">Claim not found.</p></Layout>;

  return (
    <Layout>
      <div className="mb-4">
        <Link to="/claims" className="text-sm text-brand-red hover:underline" style={{ display: 'inline-block', padding: '8px 0' }}>← {t('common.back')}</Link>
      </div>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{claim.subject}</h1>
          <p className="text-gray-500 text-sm mt-1">{claim.registration?.product?.name} · {claim.registration?.serialNumber}</p>
        </div>
        <span className={`badge ${STATUS_COLORS[claim.status] ?? ''} text-sm`}>{t(`status.${claim.status}`)}</span>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:gap-6">
        <div className="flex-1 min-w-0 space-y-4">
          {/* Original description */}
          <div className="card" style={{ padding: '12px 14px' }}>
            <p className="text-xs text-gray-400 mb-2">Original message · {claim.createdAt.slice(0, 10)}</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{claim.description}</p>
          </div>

          {/* Thread */}
          {claim.claimMessages.map(m => (
            <div key={m.id} className={`card ${m.sender === 'admin' ? 'border-brand-line bg-brand-paper' : ''}`} style={{ padding: '12px 14px' }}>
              <p className="text-xs text-gray-400 mb-2">
                {m.sender === 'admin' ? 'Montipower support' : (m.user?.firstName ?? m.user?.email ?? 'You')} · {m.createdAt.slice(0, 10)}
              </p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}

          {/* Reply form */}
          {claim.status !== 'resolved' && claim.status !== 'rejected' && (
            <form onSubmit={sendReply} className="card space-y-3">
              <label className="label">{t('claims.reply')}</label>
              <textarea className="input" rows={4} value={reply} onChange={e => setReply(e.target.value)} placeholder={t('claims.replyPlaceholder')} />
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button className="btn-primary w-full sm:w-auto" type="submit" disabled={sending || !reply.trim()}>
                  {sending ? t('common.loading') : t('claims.send')}
                </button>
                <div className="flex items-center gap-2">
                  <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => setFile(e.target.files?.[0] ?? null)} className="text-xs text-gray-500" style={{ minHeight: 44 }} />
                  {file && <button type="button" className="btn-secondary text-xs" onClick={uploadAttachment} style={{ minHeight: 44 }}>Upload</button>}
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4 md:w-72 md:flex-shrink-0">
          <div className="card text-sm space-y-2">
            <p className="font-semibold text-gray-700">Claim details</p>
            <div className="flex justify-between"><span className="text-gray-500">Filed</span><span>{claim.createdAt.slice(0, 10)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">{t('claims.priority')}</span><span className="capitalize">{claim.priority}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Claim #</span><span>{claim.id}</span></div>
          </div>

          {claim.adminNotes && (
            <div className="card text-sm bg-yellow-50 border-yellow-200">
              <p className="font-semibold text-yellow-800 mb-1">{t('claims.adminNotes')}</p>
              <p className="text-yellow-700">{claim.adminNotes}</p>
            </div>
          )}

          {claim.claimAttachments.length > 0 && (
            <div className="card text-sm">
              <p className="font-semibold text-gray-700 mb-2">{t('claims.attachments')}</p>
              <div className="space-y-2">
                {claim.claimAttachments.map(a => {
                  const isImage = /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(a.fileName);
                  const url = `/api/files/${encodeURIComponent(a.fileKey)}`;
                  return (
                    <div key={a.id}>
                      {isImage ? (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          <img src={url} alt={a.fileName}
                            style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 6, border: '1px solid #e5e5e5', cursor: 'zoom-in', objectFit: 'contain', background: '#fafafa' }} />
                          <p className="text-xs mt-1 text-gray-400 truncate">{a.fileName}</p>
                        </a>
                      ) : (
                        <a href={url} target="_blank" rel="noopener noreferrer" download={a.fileName}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg"
                          style={{ color: '#e00000', background: '#fff5f5', border: '1px solid #fecaca', textDecoration: 'none', display: 'inline-flex' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                          {a.fileName}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
